import {
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    AvatarGroup,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "AvatarGroup";

const USAGE_CODE = `<AvatarGroup
    accessibilityLabel="Project team"
    avatars={[
        { title: "Ada Lovelace" },
        { title: "Alan Turing" },
        { title: "Grace Hopper" },
        { title: "Linus Torvalds" },
        { title: "Margaret Hamilton" }
    ]}
    size="small"
/>

<AvatarGroup
    accessibilityLabel="Show team"
    isWorkWithAction={true}
    onPress={handleOpenTeam}
    avatars={members}
/>`;

const STATUS_CODE = `<AvatarGroup
    avatars={[
        { title: "Ada Lovelace", statusIndicatorType: "online" },
        { title: "Alan Turing", statusIndicatorType: "busy" }
    ]}
    isStatusIndicator={true}
/>`;

const MAX_CODE = `<AvatarGroup
    accessibilityLabel="Project team"
    avatars={members}
    max={3}
/>`;

const ADD_CODE = `<AvatarGroup
    accessibilityLabel="Project team"
    onAddPress={handleAddMember}
    avatars={members}
    max={3}
/>`;

const MEMBER_KEYS = [
    "avatarGroupPage.member1",
    "avatarGroupPage.member2",
    "avatarGroupPage.member3",
    "avatarGroupPage.member4",
    "avatarGroupPage.member5",
    "avatarGroupPage.member6",
    "avatarGroupPage.member7",
    "avatarGroupPage.member8"
] as const;

const SIZES = [
    "xxSmall",
    "xSmall",
    "small",
    "medium",
    "large",
    "xLarge"
] as const;

const STATUSES = [
    "online",
    "away",
    "busy",
    "idle",
    "offline"
] as const;

const OVERVIEW_COUNT = 6;

const TOOLTIP_COUNT = 15;

const TOOLTIP_MAX = 3;

const MAX_COUNT = 12;

const MAX_LIMITS = [
    2,
    4,
    6
] as const;

const ADD_START_COUNT = 2;

const STATUS_COUNT = 4;

const SIZE_COUNT = 3;

const MIN_COUNT = 1;

const LOADING_DELAY = 1200;

const CELL_STYLE = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex"
} as const;

const SLOT_STYLE = {
    justifyContent: "center",
    alignItems: "center",
    display: "flex"
} as const;

const SampleCell = ({
    isLarge = false,
    children,
    label
}: {
    children: ReactNode;
    isLarge?: boolean;
    label: string;
}) => {
    const {
        avatarSizes,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <div
        style={{
            ...CELL_STYLE,
            gap: spaces.spacingMd
        }}
    >
        <div
            style={{
                ...SLOT_STYLE,
                height: isLarge ? avatarSizes.xLarge : avatarSizes.large
            }}
        >
            {children}
        </div>
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("avatarGroupPage.overflowTitle"),
        id: "overflow"
    },
    {
        title: t("avatarGroupPage.maxTitle"),
        id: "max"
    },
    {
        title: t("avatarGroupPage.tooltipTitle"),
        id: "tooltip"
    },
    {
        title: t("avatarGroupPage.addTitle"),
        id: "add"
    },
    {
        title: t("avatarGroupPage.statusTitle"),
        id: "status"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const AvatarGroupPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        count,
        setCount
    ] = useState(OVERVIEW_COUNT);

    const [
        addCount,
        setAddCount
    ] = useState(ADD_START_COUNT);

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const members = MEMBER_KEYS.map((key) => ({
        title: t(key)
    }));

    const manyMembers = Array.from({
        length: TOOLTIP_COUNT
    }, (_, index) => ({
        title: t(MEMBER_KEYS[index % MEMBER_KEYS.length] ?? MEMBER_KEYS[0])
    }));

    const handleAddMember = () => {
        setAddCount(Math.min(addCount + 1, manyMembers.length));
    };

    const statusMembers = members.slice(0, STATUS_COUNT).map((member, index) => ({
        statusIndicatorType: STATUSES[index],
        ...member
    }));

    useEffect(() => {
        if(!isLoading) {
            return;
        }

        const timer = window.setTimeout(() => setIsLoading(false), LOADING_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isLoading
    ]);

    return <Article>
        <ComponentHeader
            lead={t("avatarGroupPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("avatarGroupPage.overviewText")}
            </Paragraph>
            <Preview>
                <AvatarGroup
                    accessibilityLabel={t("avatarGroupPage.teamLabel")}
                    avatars={members.slice(0, OVERVIEW_COUNT)}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("avatarGroupPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <SampleCell
                        isLarge={true}
                        label={size}
                        key={size}
                    >
                        <AvatarGroup
                            avatars={members.slice(0, SIZE_COUNT)}
                            size={size}
                        />
                    </SampleCell>)
                }
            </Preview>
        </Section>
        <Section
            title={t("avatarGroupPage.overflowTitle")}
            id="overflow"
        >
            <Paragraph>
                {t("avatarGroupPage.overflowText")}
            </Paragraph>
            <Preview
                caption={t("avatarGroupPage.overflowCaption", [
                    count
                ])}
            >
                <AvatarGroup
                    accessibilityLabel={t("avatarGroupPage.teamLabel")}
                    avatars={members.slice(0, count)}
                />
                <Button
                    onPress={() => setCount(Math.max(count - 1, MIN_COUNT))}
                    title={t("avatarGroupPage.removeMember")}
                    isDisabled={count <= MIN_COUNT}
                    spreadBehaviour="free"
                    variant="outline"
                />
                <Button
                    onPress={() => setCount(Math.min(count + 1, members.length))}
                    title={t("avatarGroupPage.addMember")}
                    isDisabled={count >= members.length}
                    spreadBehaviour="free"
                    variant="outline"
                />
            </Preview>
        </Section>
        <Section
            title={t("avatarGroupPage.maxTitle")}
            id="max"
        >
            <Paragraph>
                {t("avatarGroupPage.maxText")}
            </Paragraph>
            <Preview>
                {
                    MAX_LIMITS.map((limit) => <SampleCell
                        label={`max={${limit}}`}
                        key={limit}
                    >
                        <AvatarGroup
                            accessibilityLabel={t("avatarGroupPage.teamLabel")}
                            avatars={manyMembers.slice(0, MAX_COUNT)}
                            max={limit}
                        />
                    </SampleCell>)
                }
            </Preview>
            <CodeBlock
                code={MAX_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("avatarGroupPage.tooltipTitle")}
            id="tooltip"
        >
            <Paragraph>
                {t("avatarGroupPage.tooltipText")}
            </Paragraph>
            <Preview
                caption={t("avatarGroupPage.tooltipCaption")}
            >
                <AvatarGroup
                    accessibilityLabel={t("avatarGroupPage.teamLabel")}
                    avatars={manyMembers}
                    max={TOOLTIP_MAX}
                />
            </Preview>
        </Section>
        <Section
            title={t("avatarGroupPage.addTitle")}
            id="add"
        >
            <Paragraph>
                {t("avatarGroupPage.addText")}
            </Paragraph>
            <Preview
                caption={t("avatarGroupPage.addCaption")}
            >
                <AvatarGroup
                    accessibilityLabel={t("avatarGroupPage.teamLabel")}
                    avatars={manyMembers.slice(0, addCount)}
                    onAddPress={handleAddMember}
                    max={TOOLTIP_MAX}
                />
            </Preview>
            <CodeBlock
                code={ADD_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("avatarGroupPage.statusTitle")}
            id="status"
        >
            <Paragraph>
                {t("avatarGroupPage.statusText")}
            </Paragraph>
            <Preview>
                <AvatarGroup
                    accessibilityLabel={t("avatarGroupPage.teamLabel")}
                    isStatusIndicator={true}
                    avatars={statusMembers}
                    size="large"
                />
            </Preview>
            <CodeBlock
                code={STATUS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("avatarGroupPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("avatarGroupPage.statesCaption")}
            >
                <SampleCell
                    label="isLoading"
                >
                    <AvatarGroup
                        accessibilityLabel={t("avatarGroupPage.teamLabel")}
                        avatars={members.slice(0, OVERVIEW_COUNT)}
                        onPress={() => setIsLoading(true)}
                        isWorkWithAction={true}
                        isLoading={isLoading}
                    />
                </SampleCell>
                <SampleCell
                    label="isDisabled"
                >
                    <AvatarGroup
                        accessibilityLabel={t("avatarGroupPage.teamLabel")}
                        avatars={members.slice(0, OVERVIEW_COUNT)}
                        onPress={() => undefined}
                        isWorkWithAction={true}
                        isDisabled={true}
                    />
                </SampleCell>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("avatarGroupPage.a11yGroup"),
                    t("avatarGroupPage.a11ySummary"),
                    t("avatarGroupPage.a11yHidden"),
                    t("avatarGroupPage.a11yActionable"),
                    t("avatarGroupPage.a11yHitArea"),
                    t("avatarGroupPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("avatarGroupPage.propAvatars"),
                        type: "Array<AvatarGroupAvatar>",
                        defaultValue: "[]",
                        name: "avatars"
                    },
                    {
                        description: t("avatarGroupPage.propMax"),
                        defaultValue: "4",
                        type: "number",
                        name: "max"
                    },
                    {
                        description: t("avatarGroupPage.propOnAddPress"),
                        type: "() => void",
                        name: "onAddPress"
                    },
                    {
                        type: "\"xxSmall\" | \"xSmall\" | \"small\" | \"medium\" | \"large\" | \"xLarge\"",
                        description: t("avatarGroupPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("avatarGroupPage.propIsStatusIndicator"),
                        name: "isStatusIndicator",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("avatarGroupPage.propIsShowStatusIndicatorBorder"),
                        name: "isShowStatusIndicatorBorder",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("avatarGroupPage.propTitleAbbreviationType"),
                        type: "\"first-last\" | \"first-next\" | \"every-first\"",
                        defaultValue: "\"first-last\"",
                        name: "titleAbbreviationType"
                    },
                    {
                        description: t("avatarGroupPage.propAvatarBackgroundColor"),
                        type: "keyof ContainerContentColors",
                        name: "avatarBackgroundColor",
                        defaultValue: "\"default\""
                    },
                    {
                        type: "keyof BorderContentColors | \"transparent\"",
                        description: t("avatarGroupPage.propBorderColor"),
                        name: "borderColor"
                    },
                    {
                        description: t("avatarGroupPage.propImageProps"),
                        type: "ImageProps",
                        name: "imageProps"
                    },
                    {
                        description: t("avatarGroupPage.propIsWorkWithAction"),
                        name: "isWorkWithAction",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "(event: GestureResponderEvent) => void",
                        description: t("avatarGroupPage.propOnPress"),
                        name: "onPress"
                    },
                    {
                        description: t("avatarGroupPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("avatarGroupPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("avatarGroupPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("avatarGroupPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("avatarGroupPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph>
                {t("avatarGroupPage.membersText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("avatarGroupPage.memberTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("avatarGroupPage.memberImageUrl"),
                        name: "imageUrl",
                        type: "string"
                    },
                    {
                        description: t("avatarGroupPage.memberImageSource"),
                        type: "ImageURISource",
                        name: "imageSource"
                    },
                    {
                        description: t("avatarGroupPage.memberImage"),
                        type: "ComponentType<ImageProps>",
                        name: "image"
                    },
                    {
                        description: t("avatarGroupPage.memberIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        type: "\"primary\" | \"neutral\" | \"success\" | \"info\" | \"warning\" | \"danger\"",
                        description: t("avatarGroupPage.memberType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("avatarGroupPage.memberBackgroundColor"),
                        type: "keyof ContainerContentColors | \"transparent\"",
                        name: "backgroundColor"
                    },
                    {
                        description: t("avatarGroupPage.memberTitleColor"),
                        type: "keyof TextContentColors",
                        name: "titleColor"
                    },
                    {
                        description: t("avatarGroupPage.memberIconColor"),
                        type: "keyof ProjectColorPalette | string",
                        name: "iconColor"
                    },
                    {
                        type: "{ icon: NCoreUIKitIcon; type?: StateColorType; accessibilityLabel?: string }",
                        description: t("avatarGroupPage.memberBadge"),
                        name: "badge"
                    },
                    {
                        type: "\"online\" | \"offline\" | \"busy\" | \"away\" | \"idle\"",
                        description: t("avatarGroupPage.memberStatusIndicatorType"),
                        name: "statusIndicatorType",
                        defaultValue: "\"online\""
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("avatarGroupPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default AvatarGroupPage;
